import { evalRule, isEmptyValue } from './rules';
import {
  DISPLAY_TYPES,
  LOCATION_LEVELS,
  type Answers,
  type CompiledSchema,
  type EvaluationResult,
  type FieldError,
  type FormField,
  type LocationLevel,
} from './types';

const CODE_RE = /^[A-Z][A-Z0-9_]{1,49}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[0-9][0-9\s\-()]{6,19}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Arabic messages; frontends may translate by `rule`. */
const MSG: Record<string, string> = {
  required: 'هذا الحقل مطلوب',
  type: 'قيمة غير صالحة',
  min: 'القيمة أقل من الحد الأدنى',
  max: 'القيمة أكبر من الحد الأعلى',
  minLength: 'النص أقصر من المسموح',
  maxLength: 'النص أطول من المسموح',
  pattern: 'الصيغة غير صحيحة',
  range_order: 'قيمة "من" يجب أن تكون أصغر من أو تساوي "إلى"',
  option: 'الخيار غير موجود',
  duplicate: 'قيم مكررة',
  email: 'البريد الإلكتروني غير صحيح',
  phone: 'رقم الهاتف غير صحيح',
  date: 'التاريخ غير صحيح',
  code: 'قيمة غير صالحة',
  location_depth: 'يرجى تحديد الموقع بدقة أكبر',
  max_files: 'عدد الملفات أكبر من المسموح',
  step: 'يجب أن تكون القيمة رقماً صحيحاً',
  map_point: 'الموقع على الخريطة غير صحيح',
};

function err(field: string, rule: string, params?: Record<string, unknown>): FieldError {
  return { field, rule, message: MSG[rule] ?? MSG.type!, ...(params ? { params } : {}) };
}

function toNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

function checkNumber(field: FormField, raw: unknown, errors: FieldError[], path = field.key): number | undefined {
  const n = toNumber(raw);
  if (n === undefined) {
    errors.push(err(path, 'type'));
    return undefined;
  }
  const v = field.config.validation ?? {};
  if (field.type === 'currency' && n < 0) errors.push(err(path, 'min', { min: 0 }));
  if (v.min !== undefined && n < v.min) errors.push(err(path, 'min', { min: v.min }));
  if (v.max !== undefined && n > v.max) errors.push(err(path, 'max', { max: v.max }));
  if (v.step === 1 && !Number.isInteger(n)) errors.push(err(path, 'step'));
  return n;
}

function staticOptionValues(field: FormField): Set<string> | null {
  const opts = field.config.options;
  return opts && opts.length > 0 ? new Set(opts.map((o) => o.value)) : null;
}

/** Option values offered for these answers, or null when no option of the field is conditional. */
export function offeredOptions(field: FormField, values: Answers): string[] | null {
  const opts = field.config.options;
  if (!opts?.some((o) => o.visibleWhen)) return null;
  return opts.filter((o) => evalRule(o.visibleWhen ?? null, values)).map((o) => o.value);
}

/**
 * Validate and normalise one visible, non-empty value.
 * Returns the normalised value, or undefined when invalid (errors are pushed).
 */
function normalise(field: FormField, raw: unknown, errors: FieldError[], offered: Set<string> | null): unknown {
  const k = field.key;
  const val = field.config.validation ?? {};

  switch (field.type) {
    case 'text':
    case 'textarea': {
      if (typeof raw !== 'string') return void errors.push(err(k, 'type'));
      const s = raw.trim();
      if (val.minLength !== undefined && s.length < val.minLength) errors.push(err(k, 'minLength', { minLength: val.minLength }));
      if (val.maxLength !== undefined && s.length > val.maxLength) errors.push(err(k, 'maxLength', { maxLength: val.maxLength }));
      if (val.pattern && !new RegExp(val.pattern).test(s)) errors.push(err(k, 'pattern'));
      return s;
    }
    case 'email': {
      if (typeof raw !== 'string' || !EMAIL_RE.test(raw.trim())) return void errors.push(err(k, 'email'));
      return raw.trim().toLowerCase();
    }
    case 'phone': {
      if (typeof raw !== 'string' || !PHONE_RE.test(raw.trim())) return void errors.push(err(k, 'phone'));
      return raw.replace(/[\s\-()]/g, '');
    }
    case 'number':
    case 'currency':
      return checkNumber(field, raw, errors);

    case 'number_range': {
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return void errors.push(err(k, 'type'));
      const r = raw as Record<string, unknown>;
      const before = errors.length;
      const min = isEmptyValue(r.min) ? undefined : checkNumber(field, r.min, errors, `${k}.min`);
      const max = isEmptyValue(r.max) ? undefined : checkNumber(field, r.max, errors, `${k}.max`);
      if (min !== undefined && max !== undefined && min > max) errors.push(err(k, 'range_order'));
      if (errors.length > before) return undefined;
      return { min: min ?? null, max: max ?? null };
    }
    case 'date': {
      if (typeof raw !== 'string' || !DATE_RE.test(raw) || Number.isNaN(Date.parse(raw))) return void errors.push(err(k, 'date'));
      return raw;
    }
    case 'date_range': {
      if (typeof raw !== 'object' || raw === null) return void errors.push(err(k, 'type'));
      const r = raw as Record<string, unknown>;
      for (const b of ['from', 'to'] as const) {
        const d = r[b];
        if (!isEmptyValue(d) && (typeof d !== 'string' || !DATE_RE.test(d))) return void errors.push(err(`${k}.${b}`, 'date'));
      }
      if (typeof r.from === 'string' && typeof r.to === 'string' && r.from > r.to) return void errors.push(err(k, 'range_order'));
      return { from: r.from ?? null, to: r.to ?? null };
    }
    case 'boolean': {
      if (typeof raw === 'boolean') return raw;
      if (raw === 'true' || raw === 'false') return raw === 'true';
      return void errors.push(err(k, 'type'));
    }
    case 'radio':
    case 'dropdown': {
      if (typeof raw !== 'string') return void errors.push(err(k, 'type'));
      const allowed = offered ?? staticOptionValues(field);
      if (allowed && !allowed.has(raw)) return void errors.push(err(k, 'option'));
      return raw;
    }
    case 'checkbox_group': {
      if (!Array.isArray(raw) || raw.some((x) => typeof x !== 'string')) return void errors.push(err(k, 'type'));
      const arr = raw as string[];
      if (new Set(arr).size !== arr.length) return void errors.push(err(k, 'duplicate'));
      const allowed = offered ?? staticOptionValues(field);
      if (allowed && arr.some((x) => !allowed.has(x))) return void errors.push(err(k, 'option'));
      if (val.max !== undefined && arr.length > val.max) errors.push(err(k, 'max', { max: val.max }));
      return arr;
    }
    case 'request_type':
    case 'property_category':
    case 'property_type': {
      if (typeof raw !== 'string' || !CODE_RE.test(raw)) return void errors.push(err(k, 'code'));
      // the request types this form offers (the builder's choice); none listed = all of them
      if (field.type === 'request_type') {
        const allowed = staticOptionValues(field);
        if (allowed && !allowed.has(raw)) return void errors.push(err(k, 'option'));
      }
      return raw;
    }
    case 'property_features': {
      if (!Array.isArray(raw) || raw.some((x) => typeof x !== 'string' || !CODE_RE.test(x))) return void errors.push(err(k, 'code'));
      const arr = raw as string[];
      if (new Set(arr).size !== arr.length) return void errors.push(err(k, 'duplicate'));
      return arr;
    }
    case 'location': {
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return void errors.push(err(k, 'type'));
      const r = raw as Record<string, unknown>;
      const out: Record<string, number | null> = {};
      const idKeys = { country: 'countryId', governorate: 'governorateId', area: 'areaId', neighborhood: 'neighborhoodId' } as const;
      let gap = false;
      for (const level of LOCATION_LEVELS) {
        const v = r[idKeys[level]];
        if (isEmptyValue(v)) {
          out[idKeys[level]] = null;
          gap = true;
          continue;
        }
        const n = toNumber(v);
        if (n === undefined || !Number.isInteger(n) || n <= 0 || gap) return void errors.push(err(k, 'type'));
        out[idKeys[level]] = n;
      }
      if (out.countryId === null) return void errors.push(err(k, 'required'));
      const need: LocationLevel = field.config.requiredDepth ?? 'country';
      const depthIdx = LOCATION_LEVELS.indexOf(need);
      for (let i = 0; i <= depthIdx; i++) {
        if (out[idKeys[LOCATION_LEVELS[i]!]] === null) return void errors.push(err(k, 'location_depth', { requiredDepth: need }));
      }
      return out;
    }
    case 'map_point': {
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return void errors.push(err(k, 'type'));
      const r = raw as Record<string, unknown>;
      const lat = toNumber(r.lat);
      const lng = toNumber(r.lng);
      if (lat === undefined || lng === undefined || lat < -90 || lat > 90 || lng < -180 || lng > 180 || (lat === 0 && lng === 0)) {
        return void errors.push(err(k, 'map_point'));
      }
      return { lat: Math.round(lat * 1e6) / 1e6, lng: Math.round(lng * 1e6) / 1e6 };
    }
    case 'image_upload':
    case 'file_upload': {
      const arr = Array.isArray(raw) ? raw : [raw];
      if (arr.some((x) => typeof x !== 'string' || !UUID_RE.test(x))) return void errors.push(err(k, 'type'));
      if (new Set(arr).size !== arr.length) return void errors.push(err(k, 'duplicate'));
      const maxFiles = field.config.maxFiles;
      if (maxFiles !== undefined && arr.length > maxFiles) return void errors.push(err(k, 'max_files', { maxFiles }));
      return arr;
    }
    default:
      return undefined;
  }
}

/**
 * The single source of truth for "what is shown, what is required, what is valid".
 * Used identically by the three frontends (live preview) and the backend (authoritative).
 *
 * Rules:
 *  - fields are processed in `order`; a condition only sees values of EARLIER visible fields
 *  - a hidden field has no value: it is stripped and never validated
 *  - display fields (section/info) never carry values
 *  - unknown keys are stripped
 *  - an option with its own visibleWhen is offered only while that rule holds; a choice field
 *    with conditional options and none offered right now is hidden
 */
export function evaluate(schema: CompiledSchema, answers: Answers): EvaluationResult {
  const fields = [...schema.fields].sort((a, b) => a.order - b.order);
  const visible: Record<string, boolean> = {};
  const required: Record<string, boolean> = {};
  const values: Answers = {};
  const errors: FieldError[] = [];
  const known = new Set<string>();
  const options: Record<string, string[]> = {};

  for (const field of fields) {
    known.add(field.key);
    const offered = DISPLAY_TYPES.has(field.type) ? null : offeredOptions(field, values);
    const isVisible = evalRule(field.visibleWhen ?? null, values) && (offered === null || offered.length > 0);
    visible[field.key] = isVisible;
    if (!isVisible || DISPLAY_TYPES.has(field.type)) continue;
    if (offered) options[field.key] = offered;

    const isRequired = field.required || (field.requiredWhen ? evalRule(field.requiredWhen, values) : false);
    required[field.key] = isRequired;

    const raw = answers[field.key];
    if (isEmptyValue(raw)) {
      if (isRequired) errors.push(err(field.key, 'required'));
      continue;
    }
    const normalised = normalise(field, raw, errors, offered ? new Set(offered) : null);
    if (normalised !== undefined) values[field.key] = normalised;
  }

  const displayKeys = new Set(fields.filter((f) => DISPLAY_TYPES.has(f.type)).map((f) => f.key));
  const stripped = Object.keys(answers).filter((k) => !known.has(k) || !visible[k] || displayKeys.has(k));
  return { visible, required, values, stripped, errors, options };
}

/** Convenience for UIs: visibility map only. */
export function visibleFields(schema: CompiledSchema, answers: Answers): Record<string, boolean> {
  return evaluate(schema, answers).visible;
}
